/** Format a duration in seconds as a human-readable string: "3m 12s", "45s" */
export function formatDuration(sec: number | null): string {
  if (sec === null) return "";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

/** Format an ISO timestamp as a relative time string: "just now", "3m ago", "2h ago", "5d ago" */
export function formatRelativeTime(ts: string): string {
  try {
    const ms = Date.now() - new Date(ts).getTime();
    if (ms < 5000) return "just now";
    if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  } catch {
    return ts;
  }
}

/** Format a deployment event type as a readable label: "deployment.build_started" -> "Build Started" */
export function formatEventType(eventType: string): string {
  const parts = eventType.replace("deployment.", "").split("_");
  return parts
    .map((p, i) => (i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(" ")
    .replace(/^([a-z])/, (_, c) => c.toUpperCase());
}

/** Short SHA for display: first 7 chars, or empty if null/placeholder. */
export function shortSha(sha: string | null): string {
  if (!sha || sha === "deploy-only") return "";
  return sha.length > 7 ? sha.slice(0, 7) : sha;
}

/**
 * Derive a "failed at <Phase>" label from the first failed step in a run.
 * Returns null when no failed step is found.
 */
export function formatFailedAt(
  steps: { step: string; status: string }[] | null | undefined,
): string | null {
  if (!steps) return null;
  const failed = steps.find((s) => s.status === "failed");
  if (!failed) return null;
  const phaseLabels: Record<string, string> = {
    build: "Build",
    security_scan: "Scan",
    scan: "Scan",
    quality_gate: "Quality gate",
    deploy: "Deploy",
    verify: "Verify",
    vcs_provisioning: "VCS",
  };
  return phaseLabels[failed.step] ?? failed.step;
}
